import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import { useTerritories } from '../../context/TerritoriesContext';
import SourceBadge from '../../components/SourceBadge';
import { subscribeLeads } from '../../data/listeners/leads';
import {
  updateLeadStage, setLeadVerified, setLeadArchived,
} from '../../data/commands/leadCommands';
import { groupByStage } from '../../data/pipeline';
import { STAGES, STAGE_COLOR, leadValueOf } from '../../data/leadConstants';

const peso = (n) => `₱${Math.round(n).toLocaleString()}`;

// Pipeline Kanban — drag a card to a new stage = an audited lead write.
// `ownerFilter` (manager salesman filter) scopes the board to one salesman.
// Archived Won/Lost cards drop off the active board (CRM revisions Task 2).
export default function Pipeline({ ownerFilter = '' }) {
  const { tenantId, role, territoryIds, profile, authUser } = useAuth();
  const { openLead } = useUI();
  const { terrLabel } = useTerritories();
  const [leads, setLeads] = useState([]);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(null);

  useEffect(() => {
    const unsub = subscribeLeads(
      { tenantId, role, territoryIds },
      setLeads,
      (e) => setError(e.message || 'Could not load pipeline.'),
    );
    return unsub;
  }, [tenantId, role, territoryIds]);

  const cols = useMemo(() => {
    const active = leads.filter((l) => !l.archived && (!ownerFilter || l.ownerUid === ownerFilter));
    return groupByStage(active);
  }, [leads, ownerFilter]);
  const actor = { uid: authUser?.uid, name: profile?.name ?? profile?.email };

  async function onDrop(stage, e) {
    e.preventDefault();
    setDragOver(null);
    const leadId = e.dataTransfer.getData('text/plain');
    const lead = leads.find((l) => l.id === leadId);
    if (!lead || lead.stage === stage) return;
    try {
      await updateLeadStage(leadId, stage, actor); // onLeadWrite logs the move
    } catch (err) {
      setError(err.message || 'Could not move the deal.');
    }
  }

  async function toggleVerified(lead, e) {
    e.stopPropagation();
    try {
      await setLeadVerified(lead.id, !lead.verified, actor);
    } catch (err) {
      setError(err.message || 'Could not update verified status.');
    }
  }

  async function archive(lead, e) {
    e.stopPropagation();
    try {
      await setLeadArchived(lead.id, true, actor);
    } catch (err) {
      setError(err.message || 'Could not archive the lead.');
    }
  }

  return (
    <div>
      {error && <div className="form-error">{error}</div>}
      <div className="kboard">
        {STAGES.map((stage) => (
          <div
            key={stage}
            className={`kcol${dragOver === stage ? ' dragover' : ''}`}
            style={{ '--kcol-color': STAGE_COLOR[stage] }}
            onDragOver={(e) => { e.preventDefault(); setDragOver(stage); }}
            onDragLeave={() => setDragOver((s) => (s === stage ? null : s))}
            onDrop={(e) => onDrop(stage, e)}
          >
            <div className="kcol-head">
              <span>{stage}</span>
              <span className="ct">{cols[stage].length}</span>
            </div>
            <div className="kcol-body">
              {cols[stage].map((l) => (
                <div
                  key={l.id}
                  className="kcard"
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData('text/plain', l.id)}
                  onClick={() => openLead(l.id)}
                >
                  <div className="biz">{l.businessName}<SourceBadge lead={l} /></div>
                  <div className="meta">
                    {l.category} · {terrLabel(l.territoryId)} · score {l.leadScore}
                    <br />{peso(leadValueOf(l))}/mo
                  </div>
                  {stage === 'New' && (
                    <label className="kcard-toggle" onClick={(e) => e.stopPropagation()} title="Mark this new lead verified">
                      <input type="checkbox" checked={!!l.verified} onChange={(e) => toggleVerified(l, e)} />
                      {l.verified ? 'Verified' : 'Not verified'}
                    </label>
                  )}
                  {(stage === 'Won' || stage === 'Lost') && (
                    <button type="button" className="btn btn-ghost btn-sm kcard-archive" onClick={(e) => archive(l, e)}>
                      Archive
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="kcol-foot">{peso(cols[stage].reduce((a, l) => a + leadValueOf(l), 0))}/mo</div>
          </div>
        ))}
      </div>
    </div>
  );
}
